/**
 * Public /confirm-delivery?token=xxx
 *
 * One-click confirmation landing page reached from the T-3 email/WhatsApp
 * reminder. Verifies the token, greets the customer with the delivery
 * details, and confirms with a single big button.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertCircle, PawPrint, Package, Calendar as CalIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useLang } from '@/lib/LangContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ConfirmDeliveryPage() {
  const [params] = useSearchParams();
  const { lang } = useLang();
  const token = params.get('token') || '';

  const [state, setState] = useState('verifying'); // verifying | ready | expired | invalid | confirming | done | already
  const [info, setInfo] = useState(null);

  const L = (en, ka) => (lang === 'ka' ? ka : en);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const res = await fetch(`${API}/api/public/delivery-confirm/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (res.status === 410) { setState('expired'); return; }
        if (!res.ok) { setState('invalid'); return; }
        const data = await res.json();
        setInfo(data);
        setState(data.already_confirmed ? 'already' : 'ready');
      } catch { setState('invalid'); }
    })();
  }, [token]);

  const submit = async () => {
    setState('confirming');
    try {
      const res = await fetch(`${API}/api/public/delivery-confirm/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (res.status === 410) { setState('expired'); return; }
      if (!res.ok) { setState('invalid'); return; }
      setState('done');
    } catch { setState('invalid'); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md" data-testid="confirm-delivery-page">
        {state === 'verifying' && (
          <Center><Loader2 className="h-6 w-6 animate-spin text-[#F25C05]" /><p className="mt-3 text-sm text-[#465B70]">{L('Checking your link…', 'ბმულის შემოწმება…')}</p></Center>
        )}
        {state === 'invalid' && (
          <Center>
            <AlertCircle className="h-10 w-10 text-rose-500" />
            <h1 className="mt-3 font-display text-2xl font-bold text-[#05223D]">{L('This link is invalid', 'ეს ბმული არასწორია')}</h1>
            <p className="mt-1 text-sm text-[#465B70]">{L('Please contact SmartPaw.', 'გთხოვთ დაუკავშირდით SmartPaw-ს.')}</p>
          </Center>
        )}
        {state === 'expired' && (
          <Center>
            <AlertCircle className="h-10 w-10 text-amber-500" />
            <h1 className="mt-3 font-display text-2xl font-bold text-[#05223D]">{L('This link has expired', 'ბმულის ვადა ამოიწურა')}</h1>
            <p className="mt-1 text-sm text-[#465B70]">{L('Sign in to confirm inside your cabinet.', 'წვდომა დასადასტურებლად დავრეგისტრირდით კაბინეტში.')}</p>
            <Link to="/cabinet/login" className="mt-4 inline-block text-sm font-bold text-[#0A4D8C] underline-offset-4 hover:underline">{L('Open cabinet', 'კაბინეტის გახსნა')}</Link>
          </Center>
        )}
        {(state === 'ready' || state === 'confirming') && info && (
          <Card className="border-[#0A4D8C1A] bg-white shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-2">
                <PawPrint className="h-5 w-5 text-[#F25C05]" />
                <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#F25C05]">SmartPaw</span>
              </div>
              <h1 className="font-display text-3xl font-extrabold text-[#05223D]" data-testid="confirm-delivery-heading">
                {L(`Confirm your delivery${info.delivery.customer_name ? ', ' + info.delivery.customer_name.split(' ')[0] : ''}`, `დაადასტურეთ მიწოდება${info.delivery.customer_name ? ', ' + info.delivery.customer_name.split(' ')[0] : ''}`)}
              </h1>
              <p className="mt-2 text-sm text-[#465B70] leading-relaxed">
                {L('Please review the details below and confirm to lock in the delivery.', 'გადახედეთ დეტალებს და დაადასტურეთ მიწოდება.')}
              </p>
              <div className="mt-4 rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-4 space-y-2">
                <div className="flex items-center gap-2"><Package size={14} className="text-[#0A4D8C]" /><span className="text-sm"><strong className="text-[#05223D]">{info.delivery.product_name}</strong> × {info.delivery.qty}</span></div>
                <div className="flex items-center gap-2"><CalIcon size={14} className="text-[#0A4D8C]" /><span className="text-sm text-[#05223D]">{(info.delivery.scheduled_at || '').slice(0, 10)}</span></div>
                {info.delivery.subscription_label && (
                  <div className="text-xs text-[#7A8A99] italic">{info.delivery.subscription_label}</div>
                )}
              </div>
              <Button onClick={submit} disabled={state === 'confirming'} className="mt-6 w-full h-12 rounded-xl bg-[#F25C05] hover:bg-[#D95204] text-white font-bold" data-testid="confirm-delivery-submit">
                {state === 'confirming' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                {L('Confirm delivery', 'მიწოდების დადასტურება')}
              </Button>
            </CardContent>
          </Card>
        )}
        {(state === 'done' || state === 'already') && (
          <Center>
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <h1 className="mt-3 font-display text-2xl font-bold text-[#05223D]">{L(state === 'already' ? 'Already confirmed' : 'Delivery confirmed', state === 'already' ? 'უკვე დადასტურებულია' : 'მიწოდება დადასტურდა')}</h1>
            <p className="mt-2 text-sm text-[#465B70] max-w-sm text-center leading-relaxed">{L('Thanks! You’ll receive payment instructions shortly.', 'მადლობა! მალე ნახვედავთ გადახდის ინსტრუქციებს.')}</p>
            <Link to="/cabinet/deliveries" className="mt-4 inline-block text-sm font-bold text-[#0A4D8C] underline-offset-4 hover:underline">{L('View my deliveries', 'ჩემი მიწოდებები')}</Link>
          </Center>
        )}
      </div>
    </div>
  );
}

function Center({ children }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16">
      {children}
    </div>
  );
}
